/** Argus-only settings view for the final fleet provenance manifests. */

import { useCallback, useEffect, useState } from 'react';
import { FileText, LoaderCircle } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { Skeleton } from '@/shared/ui/skeleton';
import { useSettingsAccess } from '../access';

interface ProvenanceManifest {
  id: number | null;
  manifest: string | null;
  is_active: boolean;
  created_at: string | null;
  requested_by: string | null;
}

interface ProvenanceHistory {
  versions: ProvenanceManifest[];
}

const BASE_URL = '/api/v1/admin/argus/provenance';

function manifestWordCount(manifest: string | null): number {
  return manifest?.trim() ? manifest.trim().split(/\s+/).length : 0;
}

function formatGeneratedAt(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'Not generated yet';
}

function ManifestText({ manifest }: { manifest: string | null }) {
  return (
    <pre className="max-h-[34rem] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-4 text-sm text-foreground">
      {manifest || '(empty final manifest)'}
    </pre>
  );
}

function VersionRow({ version }: { version: ProvenanceManifest }) {
  const [open, setOpen] = useState(false);
  const words = manifestWordCount(version.manifest);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-sm">Version {version.id ?? 'unknown'}</CardTitle>
              <Badge variant="outline">{words} words</Badge>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                {open ? 'Hide manifest' : 'View manifest'}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CardDescription>
            Generated {formatGeneratedAt(version.created_at)}
            {version.requested_by ? ` by ${version.requested_by}` : ''}
          </CardDescription>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <ManifestText manifest={version.manifest} />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function ProvenanceTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const { isAdmin } = useSettingsAccess();
  const [history, setHistory] = useState<ProvenanceHistory | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/history`, { credentials: 'include' });
      if (response.ok) {
        setHistory((await response.json()) as ProvenanceHistory);
      }
    } catch {
      // The page retains its last successful view; generation has explicit error feedback.
    }
  }, []);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const runGeneration = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/generate`, { method: 'POST', credentials: 'include' });
      if (!response.ok) {
        setError('Generation failed. Check server logs.');
        return;
      }
      await fetchHistory();
    } catch {
      setError('Generation failed. Check server logs.');
    } finally {
      setGenerating(false);
    }
  };

  const versions = history?.versions ?? [];
  const active = versions.find((version) => version.is_active) ?? null;
  const activeWords = manifestWordCount(active?.manifest ?? null);

  if (agentId !== 'argus' || !isAdmin) {
    return <Navigate to={`/settings/agents/${agentId ?? ''}/config`} replace />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <CardTitle>Fleet provenance</CardTitle>
              <CardDescription>
                Argus’s final fleet map for choosing specialists to consult. It stores final manifest text only—never the
                provider response envelope or generation request payload.
              </CardDescription>
            </div>
            <Badge variant={active ? 'secondary' : 'outline'}>{active ? 'Active' : 'Not generated'}</Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-muted-foreground">Generation state</dt>
              <dd className="text-sm font-medium" aria-live="polite">
                {generating ? 'Generating final manifest' : active ? 'Ready' : 'Waiting for first run'}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-muted-foreground">Last completed</dt>
              <dd className="text-sm font-medium">{formatGeneratedAt(active?.created_at ?? null)}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-muted-foreground">Output size</dt>
              <dd className="text-sm font-medium">{active ? `${activeWords} words` : 'No output yet'}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-muted-foreground">Completed runs</dt>
              <dd className="text-sm font-medium">{versions.length}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/settings/agents/argus/prompt">
                <FileText data-icon="inline-start" />
                Review Argus prompt
              </Link>
            </Button>
            <Button onClick={runGeneration} disabled={generating}>
              {generating && <LoaderCircle className="animate-spin" data-icon="inline-start" />}
              {generating ? 'Generating final manifest…' : 'Generate final manifest'}
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <p className="text-xs text-muted-foreground">
            Generation reads the live specialist inventory and the versioned provenance instructions. The Prompt tab
            controls Argus’s authored supervisor behavior; generated manifest text is not appended to that prompt.
          </p>
        </CardContent>
      </Card>

      {history === null ? (
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-44 w-full" />
          </CardContent>
        </Card>
      ) : active ? (
        <Card>
          <CardHeader className="gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>Latest generated reference manifest</CardTitle>
              <Badge variant="secondary">Version {active.id ?? 'unknown'}</Badge>
            </div>
            <CardDescription>
              Generated {formatGeneratedAt(active.created_at)}
              {active.requested_by ? ` by ${active.requested_by}` : ''}. This is the final generated text retained for
              fleet-reference history; it is not the current prompt input.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ManifestText manifest={active.manifest} />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>No fleet map yet</CardTitle>
            <CardDescription>
              Generate a manifest after adding, removing, or materially changing a specialist’s scope.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {versions.length > 1 && (
        <section className="flex flex-col gap-3" aria-labelledby="provenance-history-heading">
          <div className="flex flex-col gap-1">
            <h3 id="provenance-history-heading" className="text-sm font-semibold text-foreground">
              Previous final manifests
            </h3>
            <p className="text-sm text-muted-foreground">Compare earlier fleet maps without reopening generation inputs.</p>
          </div>
          {versions
            .filter((version) => !version.is_active)
            .map((version) => (
              <VersionRow key={version.id} version={version} />
            ))}
        </section>
      )}
    </div>
  );
}
